<script setup lang="tsx">
  import { PencilIcon } from '@heroicons/vue/solid';
  import { get, post } from '~/services/donationScreen';
  import type { ResponseStatus } from '~/types';
  import InfoRow from '~/components/UI/Drawers/InfoRow.vue';
  import InfoSection from '~/components/UI/Drawers/InfoSection.vue';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import { checkValueAndLength } from '~/functions/stringUtilities';
  import RoleBasedLinkButton from '~/components/UI/Others/RoleBasedLinkButton.vue';
  import ComponentSectionRow from '~/components/UI/Drawers/ComponentSectionRow.vue';
  import Attachments from '../../Others/Attachments.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import AttachmentUpdates from '../RoutedDrawerComponents/AttachmentUpdates.vue';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import type { BudgetsDrawerType } from '~/types/budgets/budgetsDrawer';
  import BudgetLineItem from '../RoutedDrawerComponents/BudgetLineItem.vue';
  import DenyBudgetModal from '../../Modals/DenyBudgetModal.vue';
  import ApproveBudgetModal from '../../Modals/ApproveBudgetModal.vue';
  import OverviewCard from '~/components/UI/Others/OverviewCard.vue';
  import CloseBudgetModal from '../../Modals/CloseBudgetModal.vue';
  import OpenBudgetModal from '../../Modals/OpenBudgetModal.vue';
  import Approvals from '../RoutedDrawerComponents/Approvals.vue';

  const { onClose, identifier } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();
  const denyBudgetModal = ref<boolean>(false);
  const approveBudgetModal = ref<boolean>(false);
  const closeBudgetModal = ref<boolean>(false);
  const approvalStatus = ref<string>('');
  const currentApprover = ref<boolean>(false);
  const isFinalApprover = ref<boolean>(false);
  const isClosed = ref<boolean>(false);
  const allowToCurrentApprover = ref<boolean>(true);
  const { role } = useAuth();
  const openBudgetModal = ref<boolean>(false);
  const data = ref<BudgetsDrawerType>({} as BudgetsDrawerType);
  const budgetAgainstExpensesNotApproved = ref<boolean>(false);
  const responseState = ref<ResponseStatus>('idle');
  const submitBudgeStatus = ref<ResponseStatus>('idle');

  const extractedNames = computed(() => {
    if (!data.value?.selected_budget?.budget_members) return [];

    return data.value?.selected_budget?.budget_members
      ?.split(',')
      .filter((entry: string) => entry.trim()) // Remove empty entries
      .map((entry: string) => {
        const nameParts = entry.trim().split('_');
        nameParts.pop();
        return nameParts.join(' ');
      });
  });

  const submitBudgetForApproval = async () => {
    try {
      submitBudgeStatus.value = 'pending';
      const response = await post<any>('/v1/submit_budget_for_approval', {
        data: { uid: identifier }
      });
      if (response.data && response.data.status === 'success') {
        submitBudgeStatus.value = 'success';
        fetchData();
        openSuccessfulCustomNotification(response.data.message);
      } else if (response.data && response.data.status == 'error') {
        openFailedCustomNotification(response.data.message);
      }
    } catch (error: any) {
      console.error(error);
      submitBudgeStatus.value = 'error';
      openFailedCustomNotification('Error while submitting budget for approval!');
    }
  };

  const showDenyBudgetModal = () => {
    denyBudgetModal.value = true;
  };

  const hideDenyBudgetModal = () => {
    denyBudgetModal.value = false;
    fetchData(); // Refresh data after closing modal
  };

  const showApproveBudgetModal = () => {
    approveBudgetModal.value = true;
  };

  const hideApproveBudgetModal = () => {
    approveBudgetModal.value = false;
    fetchData(); // Refresh data after closing modal
  };

  const showCloseBudgetModal = () => {
    closeBudgetModal.value = true;
  };

  const hideCloseBudgetModal = () => {
    closeBudgetModal.value = false;
  };

  const showOpenBudgetModal = () => {
    openBudgetModal.value = true;
  };

  const hideOpenBudgeModal = () => {
    openBudgetModal.value = false;
    fetchData(); // Refresh data after closing modal
  };

  const fetchData = async () => {
    responseState.value = 'pending';
    try {
      const response = await get<BudgetsDrawerType>(`v1/get_budget/${identifier}`);
      if (response.status === 200) {
        data.value = response.data;
        responseState.value = 'success';
        approvalStatus.value = data.value.selected_budget.approval_status;
        currentApprover.value = data.value.selected_budget?.is_current_approver;
        isClosed.value = data.value.selected_budget?.is_closed;
        isFinalApprover.value = data.value.selected_budget?.is_final_approver;
        budgetAgainstExpensesNotApproved.value = data.value.budget_against_not_approved_expense;
        const status = data?.value?.selected_budget?.approval_status;
        allowToCurrentApprover.value = !['Not submitted for approval', 'Approved', 'draft', '0 of 0'].includes(status)
          ? data.value.selected_budget?.is_current_approver
          : true;
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isAdmin = rolesList.includes('admin');
    const isBudgetAdmin = rolesList.includes('budget_admin');
    const isBudgetSubmitter = rolesList.includes('budget_submitter');
    const isBudgetApprover = rolesList.includes('budget_approver');
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNoApprover = approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = isClosed?.value;
    const isBudgetControlAdmin = rolesList.includes('budget_approval_control_admin');

    if (!isApproved && !isNotSubmitted && !isNoApprover && (isAdmin || isBudgetAdmin) && isBudgetControlAdmin) {
      return false;
    }

    if ((isBudgetSubmitter || isAdmin || isBudgetAdmin) && isNoApprover) {
      return false;
    }

    if (isNotSubmitted && isBudgetSubmitter && isAdvanceCancelled) {
      return false;
    }

    if (isBudgetSubmitter && isNotSubmitted && !isAdvanceCancelled) {
      return false;
    }

    if (isBudgetAdmin && isNotSubmitted) {
      return false;
    }

    if (isBudgetAdmin && isApproved) {
      return false;
    }

    if (isBudgetSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isBudgetAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    if (!isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return true;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if ((isBudgetApprover || isBudgetAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isBudgetApprover && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    if (isBudgetApprover && isApproved && !isAdvanceCurrentApprover) {
      return true;
    }

    if ((isBudgetApprover || isAdmin) && isApproved && !isLastApprover) {
      return false;
    }

    return true;
  });

  const isSubmitDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isAdmin = rolesList.includes('admin');
    const isBudgetAdmin = rolesList.includes('budget_admin');
    const isBudgetSubmitter = rolesList.includes('budget_submitter');
    const isBudgetApprover = rolesList.includes('budget_approver');
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved' || approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = isClosed?.value;
    const isBudgetControlAdmin = rolesList.includes('budget_approval_control_admin');

    if (isNotSubmitted && (isBudgetAdmin || isAdmin) && isBudgetControlAdmin) {
      return false;
    }

    if (!isNotSubmitted && !isAdvanceCancelled && !isApproved) {
      return true;
    }

    if (isNotSubmitted && isBudgetSubmitter) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isBudgetSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover && isAdvanceCancelled) {
      return true;
    }

    if (isBudgetAdmin && isNotSubmitted) {
      return false;
    }

    if ((isBudgetApprover || isBudgetAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if ((isBudgetApprover || isBudgetAdmin || isAdmin) && isApproved && isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isNotSubmitted && !isAdvanceCancelled) {
      return true;
    }

    return true;
  });

  const isCancelDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseAdmin = rolesList.includes('admin');
    const isBudgetAdmin = rolesList.includes('budget_admin');
    const isBudgetSubmitter = rolesList.includes('budget_submitter');
    const isBudgetApprover = rolesList.includes('budget_approver');
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNotApprover = approvalStatus?.value === '0 of 0';

    if (isNotSubmitted && isBudgetSubmitter) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isBudgetSubmitter && isApproved) {
      return true;
    }

    if (isBudgetSubmitter && isNotApprover) {
      return false;
    }

    if (isExpenseAdmin && isNotSubmitted) {
      return false;
    }

    if (isBudgetAdmin && isNotSubmitted) {
      return false;
    }

    if ((isBudgetApprover || isExpenseAdmin) && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if ((isBudgetApprover || isBudgetAdmin || isExpenseAdmin) && isApproved && isLastApprover) {
      return false;
    }

    if ((isBudgetApprover || isBudgetAdmin || isExpenseAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (!isNotSubmitted && !isApproved && isAdvanceCurrentApprover && isBudgetApprover) {
      return false;
    }

    if (isExpenseAdmin && !isApproved && !isNotSubmitted) {
      return false;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isExpenseAdmin && isApproved) {
      return false;
    }

    if (isBudgetAdmin && isApproved) {
      return false;
    }

    if (isBudgetAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    if (isBudgetApprover && isApproved && !isAdvanceCurrentApprover) {
      return true;
    }

    if ((isBudgetApprover || isExpenseAdmin) && isApproved && !isLastApprover) {
      return false;
    }

    return true;
  });

  const OpenBudgetAccess = computed(() => {
    const roles = role.value;

    const hasBaseRole = roles.includes('budget_admin') || roles.includes('admin');

    const hasApprovalControl = roles.includes('budget_approval_control_admin');

    return hasBaseRole && hasApprovalControl;
  });

  onMounted(fetchData);
</script>

<template>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Budget Information"
    :open="identifier !== null"
    width="1200"
    @close="onClose"
  >
    <template #extra>
      <div v-if="!data?.selected_budget?.is_closed" class="flex gap-2">
        <RoleBasedButton
          component-name="submitActionButton"
          :disabled="isSubmitDisabled"
          :loading="submitBudgeStatus === 'pending' || responseState === 'pending'"
          active-class="!text-white hover:!text-white bg-blue-700 hover:bg-blue-600 disabled:!text-blue-300 disabled:border-blue-300 disabled:bg-blue-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-blue-700 hover:!border-blue-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-blue-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-blue-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="submitBudgetForApproval"
        >
          Submit For Approval
        </RoleBasedButton>
        <RoleBasedLinkButton
          :to="{ path: '/budgets/edit-budget', query: { identifier } }"
          component-name="editActionButton"
          :disabled="isEditDisabled"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
        >
          <PencilIcon class="h-4 w-4" />
          Edit Budget
        </RoleBasedLinkButton>
        <RoleBasedButton
          component-name="closeActionButton"
          :loading="responseState === 'pending'"
          :disabled="isCancelDisabled"
          active-class="!text-white hover:!text-white bg-red-700 hover:bg-red-600 disabled:!text-red-300 disabled:border-red-300 disabled:bg-red-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-red-700 hover:!border-red-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-red-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-red-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showCloseBudgetModal"
        >
          Close Budget
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approveActionButton"
          :loading="responseState === 'pending'"
          :disabled="
            data?.selected_budget?.approval_status == 'Not submitted for approval' ||
            !data?.selected_budget?.is_current_approver
          "
          active-class="!text-white hover:!text-white bg-red-700 hover:bg-red-600 disabled:!text-red-300 disabled:border-red-300 disabled:bg-red-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-red-700 hover:!border-red-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-red-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-red-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showDenyBudgetModal"
        >
          Deny Budget
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approveActionButton"
          :loading="responseState === 'pending'"
          :disabled="
            data?.selected_budget?.approval_status == 'Not submitted for approval' ||
            !data?.selected_budget?.is_current_approver
          "
          active-class="!text-white hover:!text-white bg-green-700 hover:bg-green-600 disabled:!text-green-300 disabled:border-green-300 disabled:bg-green-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-green-700 hover:!border-green-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-green-500 gap-1 text-sm font-medium py-1.5 px-2 justify-center !bg-green-100 border !border-green-500 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showApproveBudgetModal"
        >
          Approve Budget
        </RoleBasedButton>
      </div>
      <div v-else-if="OpenBudgetAccess">
        <RoleBasedButton
          component-name="closeActionButton"
          :loading="responseState === 'pending'"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showOpenBudgetModal"
        >
          Re-Open Budget
        </RoleBasedButton>
      </div>
      <p v-else class="text-base font-bold text-red-600">Budget is Closed</p>
    </template>
    <DrawerLayout
      :response-state="responseState"
      :main-display="checkValueAndLength(data?.selected_budget?.name) || '-'"
      :sub-display="checkValueAndLength(data?.selected_budget?.budget_id) || '-'"
      :side-display="formatToIndianCurrency(data?.selected_budget?.amount)"
      @close="onClose"
    >
      <template #column1>
        <div class="p-3 flex flex-col gap-3">
          <a-alert
            v-if="data?.selected_budget?.approval_status === 'Approved'"
            message="Budget is Approved"
            type="success"
            show-icon
          />
          <a-alert
            v-if="
              !['Not submitted for approval', 'Approved', '0 of 0'].includes(data?.selected_budget?.approval_status)
            "
            message="Budget is Pending Approval"
            type="warning"
            show-icon
          />
          <a-alert
            v-if="!allowToCurrentApprover"
            message="You are not a current approver for this budget!"
            type="error"
            show-icon
          />
        </div>
        <InfoSection>
          <InfoRow label="Budget Financial Year" :value="checkValueAndLength(data?.selected_budget?.financial_year)" />
          <InfoRow label="Description" :value="checkValueAndLength(data?.selected_budget?.description)" />
          <ComponentSectionRow label="Budget Members" class="px-3 py-2">
            <div class="flex gap-3 flex-wrap">
              <p v-for="name in extractedNames" :key="name" class="bg-zinc-100 px-1 py-0.5 rounded-md">
                {{ name }}
              </p>
            </div>
          </ComponentSectionRow>
        </InfoSection>
        <BudgetLineItem
          :items="data?.selected_budget?.line_items"
          :budget-id="data?.selected_budget?.budget_id"
          :budget-name="data?.selected_budget?.name"
          :budget-financial-year="data?.selected_budget?.financial_year"
        />
      </template>
      <template #column2>
        <Attachments :items="data?.selected_budget?.attachments_data" :table="data?.selected_budget?.table" />

        <OverviewCard title="Budget Quick Links">
          <div class="flex justify-between items-center">
            <div class="flex gap-2 items-center">
              <p
                class="flex h-5 w-5 shrink-0 grow-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 xs-medium"
              >
                {{ data?.quick_links?.no_of_expenses_received }}
              </p>
              <p class="sm-medium text-zinc-500">Expenses</p>
            </div>
            <div class="flex gap-3 items-center">
              <a-tooltip title="View Expenses">
                <nuxt-link
                  v-if="data?.quick_links?.no_of_expenses_received > 0"
                  :to="{
                    path: '/expenses',
                    query: { budget: data?.selected_budget.budget_id, lineItem: 'yes' }
                  }"
                  class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                  target="_blank"
                >
                  View </nuxt-link
                ><span v-else class="p-0 border-none shadow-none !bg-transparent text-gray-400 cursor-not-allowed">
                  View
                </span>
              </a-tooltip>
              <!-- <nuxt-link
                :to="{
                  path: '/expenses/add-new-expense'
                }"
                class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                target="_blank"
              >
                <PlusIcon class="w-5 h-5 text-blue-700" />
              </nuxt-link> -->
            </div>
          </div>
        </OverviewCard>

        <AuditLogs :items="data?.audit_log_data" title="Budget" />
        <AttachmentUpdates :items="data?.attachment_log_data" />
        <Approvals
          :items="data?.selected_budget?.approvers_list"
          :creator="data?.selected_budget?.budget_creator.split('_').pop() || ''"
          :draft-status="data?.selected_budget?.draft_status"
          status-of="budget"
        />
      </template>
    </DrawerLayout>
  </a-drawer>
  <DenyBudgetModal :visible="denyBudgetModal" :close="hideDenyBudgetModal" :budget-id="identifier" />
  <ApproveBudgetModal :visible="approveBudgetModal" :close="hideApproveBudgetModal" :budget-id="identifier" />
  <CloseBudgetModal
    :visible="closeBudgetModal"
    :close="hideCloseBudgetModal"
    :budget-id="identifier"
    :budget-against-expenses-not-approved="budgetAgainstExpensesNotApproved"
  />
  <OpenBudgetModal :visible="openBudgetModal" :close="hideOpenBudgeModal" :budget-id="identifier" />
</template>

<style>
  @import '~/assets/css/donationDrawer.css';
</style>
